// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx, ParamComponent} from 'panel';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {debounce} from 'common/util/lodash-wrapped';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {reporter} from 'common/report/error-reporting';
import {BrowseEntityTypeFilter, searchUtils} from 'common/report/util/search';
import {
  BrowseTrackedEvent,
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  getTrackingPropertiesForResult,
  getTrackingPropertiesForResultsLoad,
  SearchSource,
  SearchTrackedProperty,
} from 'common/report/util/search/tracking';
import {getDashboardAllowedReports} from 'common/report/util/boards';
import {
  DEFAULT_SEARCH_QUERY_STRING,
  getOrderedSearchContextDisplayOptions,
  getReportNameFromSearchContext,
} from 'common/report/util/search';
import {
  RecentSearchItem,
  RecentSearchItemType,
  SearchOptionTranslationKey,
  SearchQueryFilters,
  SearchResult,
  SearchSortKey,
  SearchSortOption,
  SearchSortOrder,
} from 'common/report/util/search/types';
import {EntityTableEl} from 'common/widgets/entity-collection/entity-table';
import {IronContextRegistry} from 'common/report/context-registry';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {BrowseMode} from 'common/widgets/browse/types';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {TextHighlightEl} from 'common/components/text-highlight';
import {ToggleEl} from 'common/components/toggle';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {LocalStorageKey} from 'common/report/constants';
import {panel2react} from 'common/util/react';
import {SpinnerEl} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {TemplateType} from 'common/report/dashboards/types';
import {shouldOpenInNewTab} from 'common/util/dom';
import {getUserFlags} from 'common/report/queries/app-api/user-flags';
import ConfettiImage from 'common/assets/images/starter-board/starter-confetti.png';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {isGlobalSidenavEnabled} from 'iron/routes/global-sidenav/utils';
import {SearchInputEl} from 'common/widgets/search-input';
import {InputTheme} from 'common/components/input/types';

import {EntityGalleryEl} from '../entity-collection/entity-gallery';
import {BrowseQueryParams, FilterType, ShareStatusFilter} from './types';
import {CAPTURED_TRACKING} from './tracking';
import css from './index.inline.styl';
import {BrowseFilterEl} from './browse-filters';
import {BrowseSuggestedCreatorsEl} from './browse-suggested-creators';
import browseFilterCss from './browse-filters/index.module.styl?inline';
import {ErrorEmptyStateEl, NoResultsEmptyStateEl} from './empty-state-react';

const DEFAULT_QUERY_PARAMS = {
  searchedText: DEFAULT_SEARCH_QUERY_STRING,
  filter: {
    [FilterType.CreatorEmail]: [],
    [FilterType.ShareStatus]: [],
    [FilterType.Report]: [],
    [FilterType.Event]: [],
  },
  sort: SearchSortOption.Popularity,
};

export interface BrowseParams {
  creatorFilter?: string;
  entityTypeFilter: BrowseEntityTypeFilter;
  eventNameFilter?: string;
  mode: BrowseMode;
  projectId: number;
  onCreateDashboardClicked?: () => void;
  onOpenFoundationalTemplateModalClicked?: (options: {entryPoint: string}) => void;
  onRedirectToEntity?: () => void;
  onSearchedTextUpdate?: (searchText: string) => void;
  onSelect?: (result: SearchResult) => void;
  searchText?: string;
  userId: number;
  sort?: SearchSortOption;
}

type JsxProps = {
  params: BrowseParams;
};

enum BrowseQueryState {
  Done = `done`,
  Failed = `failed`,
  Loading = `loading`,
  LoadingMore = `loading_more`,
}

enum BrowseResultsMode {
  Table = `table`,
  Gallery = `gallery`,
}

type BrowseState = {
  lastSearchedText: string;
  name: string;
  entities: SearchResult[];
  queryParams: BrowseQueryParams;
  queryRequestIndex: number;
  queryState: BrowseQueryState;
  resultsMode: BrowseResultsMode;
  resultsLimit: number;
  showStarterBoardPrompt: boolean;
};

export type BrowsePersistedState = {
  queryParams: Partial<BrowseQueryParams>;
  resultsMode: BrowseResultsMode;
};

const FilterLabel = ({cmp}: {cmp: Browse}) => {
  const numActiveFilters = cmp.getNumActiveFilters();

  return (
    <div sel='.filter-label'>
      {cmp.t(`discover.filter.filter-label`)}
      {numActiveFilters > 0 && (
        <TextHighlightEl
          sel='.filter-label-num'
          attrs={{
            length: `wide`,
            text: numActiveFilters.toString(),
          }}
        />
      )}
    </div>
  );
};

const SortControl = ({cmp}: {cmp: Browse}) => {
  let sortOptions: SearchSortOption[];
  if (cmp.state.lastSearchedText !== DEFAULT_SEARCH_QUERY_STRING) {
    sortOptions = [
      SearchSortOption.Relevance,
      SearchSortOption.Popularity,
      SearchSortOption.LastModified,
      SearchSortOption.TitleAscending,
      SearchSortOption.TitleDescending,
    ];
  } else {
    sortOptions = [
      SearchSortOption.Popularity,
      ...(cmp.getContext(`projectTopCreators`).hasViewedContent() ? [SearchSortOption.RecentlyViewed] : []),
      SearchSortOption.LastModified,
      SearchSortOption.TitleAscending,
      SearchSortOption.TitleDescending,
    ];
  }

  return (
    <SelectEl
      sel='.sort-menu'
      attrs={{
        elref: `sortMenu`,
        icon: {leftIcon: `sort`},
        label: cmp.t(SearchOptionTranslationKey[cmp.state.queryParams.sort]),
        'menu-width': 150,
        theme: SelectTheme.Tertiary,
      }}
      props={{
        sections: [
          {
            items: sortOptions.map((sortOption) => ({
              label: cmp.t(SearchOptionTranslationKey[sortOption]),
              value: sortOption,
              isSelected: cmp.state.queryParams.sort === sortOption,
            })),
          },
        ],
      }}
      on={{
        change: (ev) => {
          cmp.track(BrowseTrackedEvent.Sort, {'New Sort Option': ev.detail.item.value});
          cmp.updateQueryParams({sort: ev.detail.item.value});
        },
      }}
    />
  );
};

const BrowseResultsHeader = ({cmp}: {cmp: Browse}) => {
  const isLoading = cmp.state.queryState === BrowseQueryState.Loading;
  const entitiesCount = cmp.state.entities?.length || 0;
  const resultsLabel = entitiesCount > 0 ? cmp.t(`discover.results.results-with-count`, {count: entitiesCount}) : ``;

  return (
    <div sel='.browse-results-header'>
      <div sel='.browse-results-count'>{!isLoading && resultsLabel}</div>
      <div sel='.browse-results-controls'>
        <div sel='.browse-results-sort'>
          <SortControl cmp={cmp} />
        </div>
        {cmp.isFullPageMode() && (
          <div sel='.browse-results-filter'>
            <ToggleEl
              attrs={{
                size: ToggleSize.Medium,
                theme: ToggleTheme.SegmentedController,
                selected: cmp.state.resultsMode,
                options: [
                  {label: ``, icon: `icon-grid`, value: BrowseResultsMode.Gallery},
                  {label: ``, icon: `type-list`, value: BrowseResultsMode.Table},
                ],
                elref: `resultsModeToggle`,
              }}
              on={{
                select: (ev: CustomEvent) => {
                  cmp.track(BrowseTrackedEvent.SwitchedResultMode, {
                    [SearchTrackedProperty.ResultMode]: ev.detail.selected,
                  });
                  cmp.update({resultsMode: ev.detail.selected as BrowseResultsMode});
                  cmp.updatePersistedState();
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const BrowseResults = ({cmp}: {cmp: Browse}) => {
  const {queryParams, queryState} = cmp.state;
  const isLoading = queryState === BrowseQueryState.Loading;

  if (cmp.state.queryState === BrowseQueryState.Failed) {
    return <ErrorEmptyStateEl />;
  }

  if (!isLoading && !cmp.state.entities?.length) {
    return <NoResultsEmptyStateEl cmp={cmp} />;
  }

  return (
    <div sel='.browse-results-table'>
      {cmp.state.resultsMode === BrowseResultsMode.Table ? (
        <EntityTableEl
          params={{
            mode: cmp.params.mode,
            isLoading: queryState === BrowseQueryState.Loading,
            nameLabel: queryParams.sort,
            userId: cmp.params.userId,
            entities: cmp.state.entities,
            onEntityClick: (entity) => cmp.handleEntityClick(entity),
            onEntityContextMenu: (entity) => cmp.handleEntityContextMenu(entity),
            onEntitySelect: (entity) => cmp.params.onSelect?.(entity),
            onEntityRedirect: cmp.params.onRedirectToEntity,
          }}
        />
      ) : (
        <EntityGalleryEl
          params={{
            mode: cmp.params.mode,
            isLoading: queryState === BrowseQueryState.Loading,
            nameLabel: queryParams.sort,
            userId: cmp.params.userId,
            entities: cmp.state.entities,
            onEntityClick: (entity) => cmp.handleEntityClick(entity),
            onEntityContextMenu: (entity) => cmp.handleEntityContextMenu(entity),
          }}
          attrs={{
            elref: `entityGallery`,
          }}
        />
      )}
      {queryState === BrowseQueryState.LoadingMore && (
        <div sel='.browse-results-spinner'>
          <SpinnerEl attrs={{size: SpinnerSize.Large}} />
        </div>
      )}
    </div>
  );
};

const StarterBoardPrompt = ({cmp}: {cmp: Browse}) => {
  const implementedData = cmp.getCurrentProject()?.hasIntegratedArb;
  cmp.track(BrowseTrackedEvent.StarterBoardPromptViewed, {
    has_integrated_arb: implementedData,
  });
  return (
    <div sel='.starter-board-prompt-container'>
      {implementedData ? (
        <>
          <div sel='.text-area'>
            <div sel='.title'>Your Starter Board is ready!</div>
            <div sel='.description'>
              View this board to learn about:
              <ul>
                <li>User activity</li>
                <li>Event volume</li>
                <li>User retention</li>
              </ul>
            </div>
            <ButtonEl
              sel='.view-starter-board-button'
              attrs={{theme: ButtonTheme.Primary}}
              on={{click: (ev) => cmp.redirectToStarterBoard(ev)}}
            >
              View Starter Board
            </ButtonEl>
          </div>
          <div sel='.image-wrapper'>
            <img sel='.confetti' attrs={{src: ConfettiImage}} />
            <div sel='.image-content' />
          </div>
        </>
      ) : (
        <>
          <div sel='.text-area'>
            <div sel='.title'>Get to know your product</div>
            <div sel='.description'>
              The Starter Board automatically highlights user activity, total events, and customer retention across your
              entire product
            </div>
            <ButtonEl
              sel='.view-starter-board-button'
              attrs={{theme: ButtonTheme.Secondary}}
              on={{click: (ev) => cmp.redirectToStarterBoard(ev)}}
            >
              View Starter Board
            </ButtonEl>
          </div>
          <div sel='.image-wrapper'>
            <div sel='.image-content' />
          </div>
        </>
      )}
    </div>
  );
};

export class Browse extends ParamComponent<BrowseParams, BrowseState, IronContextRegistry> {
  private declare persistedStateStorage: UserLocalStorageHelper<BrowsePersistedState>;

  get config() {
    return {
      contexts: [
        `currentProject` as const,
        `sessionStorageManager` as const,
        `projectTopCreators` as const,
        `urlManager` as const,
        `i18n` as const,
      ],
      params: {
        creatorFilter: String,
        emptyMessage: String,
        entityTypeFilter: String,
        eventNameFilter: String,
        mode: String,
        onCreateDashboardClicked: Function,
        onOpenFoundationalTemplateModalClicked: Function,
        onRedirectToEntity: Function,
        onSearchedTextUpdate: Function,
        onSelect: Function,
        projectId: Number,
        searchText: String,
        userId: Number,
        sort: String,
      },
      useShadowDom: true,
      css: css + browseFilterCss,
      defaultState: {
        entities: [],
        lastSearchedText: DEFAULT_SEARCH_QUERY_STRING,
        name: ``,
        open: false,
        queryParams: {
          filter: {
            [FilterType.CreatorEmail]: [],
            [FilterType.Report]: [],
            [FilterType.ShareStatus]: [],
            [FilterType.Event]: [],
          },
          sort: SearchSortOption.Popularity,
          searchedText: DEFAULT_SEARCH_QUERY_STRING,
        },
        queryRequestIndex: 0,
        queryState: BrowseQueryState.Loading,
        resultsMode: BrowseResultsMode.Table,
        resultsLimit: 100,
        showStarterBoardPrompt: false,
      },
      template: () => {
        const projectId = this.params.projectId;
        const title = this.getTitle();

        return (
          <div sel='.browse-container' class={{'is-full-page': this.isFullPageMode(), 'is-add-mode': this.isAddMode()}}>
            {title && <h2 sel='.browse-title'>{title}</h2>}
            <div sel='.browse-sections-container'>
              <div sel='.browse-main-section'>
                {this.shouldShowSearchInput() && (
                  <section
                    sel='.browse-header'
                    class={{'global-sidenav-enabled': isGlobalSidenavEnabled(this.getCurrentProject())}}
                  >
                    <SearchInputEl
                      params={{
                        searchedText: this.params.searchText,
                        onSearchedTextUpdate: (text) => this.params.onSearchedTextUpdate?.(text),
                        theme: InputTheme.Default,
                        placeholder: this.t(`common.search`),
                      }}
                    />
                  </section>
                )}
                {/* We show the suggested creators in full page mode, only before the user makes a query */}
                {this.isFullPageMode() && this.getNumActiveFilters() === 0 && (
                  <div sel='.browse-suggested-creators'>
                    <BrowseSuggestedCreatorsEl
                      params={{
                        onCreatorClick: (creator, idx) => {
                          this.updateQueryParams({
                            filter: {...this.state.queryParams.filter, [FilterType.CreatorEmail]: [creator.email]},
                          });
                          this.track(BrowseTrackedEvent.SuggestedCreatorSelected, {position: idx});
                        },
                        userId: this.params.userId,
                      }}
                    />
                  </div>
                )}
                {this.hasStarterBoard() && this.state.showStarterBoardPrompt && <StarterBoardPrompt cmp={this} />}
                <div sel='.browse-results'>
                  <BrowseResultsHeader cmp={this} />
                  <BrowseResults cmp={this} />
                </div>
              </div>
              <div
                sel='.browse-filter-section'
                class={{'global-sidenav-enabled': isGlobalSidenavEnabled(this.getCurrentProject())}}
              >
                <div sel='.browse-filter-section-header'>
                  <FilterLabel cmp={this} />
                  {this.getNumActiveFilters() > 0 && (
                    <ButtonEl
                      sel='.clear-button'
                      attrs={{theme: ButtonTheme.Tertiary}}
                      on={{click: () => this.resetFilters()}}
                    >
                      {this.t(`discover.filter.clear-all`)}
                    </ButtonEl>
                  )}
                </div>
                <BrowseFilterEl
                  params={{
                    isAddMode: this.isAddMode(),
                    filter: this.state.queryParams.filter,
                    onFilterChange: (filter) => this.updateQueryParams({filter}),
                    onTrackEvent(event, props) {
                      mixpanel.track(event, {
                        ...props,
                        project_id: projectId,
                      });
                    },
                    userId: this.params.userId,
                  }}
                />
              </div>
            </div>
          </div>
        );
      },
      hooks: {
        preUpdate: (_stateUpdate, paramsUpdate?: BrowseParams) => {
          if (
            (!isNil(paramsUpdate?.searchText) && paramsUpdate.searchText !== this.params.searchText) ||
            !isNil(paramsUpdate?.creatorFilter) ||
            !isNil(paramsUpdate?.entityTypeFilter) ||
            !isNil(paramsUpdate?.eventNameFilter)
          ) {
            this.setDefaultState(paramsUpdate);
          }
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  getCurrentProject(): CurrentProject | undefined {
    return this.getContext(`currentProject`).get().value;
  }

  getNumActiveFilters() {
    return Object.values(this.state.queryParams.filter).reduce((acc, curr) => {
      return acc + curr.length;
    }, 0);
  }

  async connectedCallback() {
    super.connectedCallback();

    this.persistedStateStorage = new UserLocalStorageHelper({
      namespace: LocalStorageKey.BrowsePersistedState,
      context: this.getContext(`sessionStorageManager`),
    });

    this.setDefaultState(this.params);
    this.track(BrowseTrackedEvent.OpenDiscover);
    await this.showStarterBoardPrompt();

    window.addEventListener(`scroll`, this.handleWindowScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(`scroll`, this.handleWindowScroll);
  }

  isAddMode() {
    return this.params.mode === BrowseMode.Add;
  }

  isFullPageMode() {
    return this.params.mode === BrowseMode.Discover;
  }

  shouldShowSearchInput() {
    return !this.isFullPageMode() || isGlobalSidenavEnabled(this.getCurrentProject());
  }

  handleWindowScroll = debounce(async () => {
    if (
      window.scrollY + window.innerHeight > document.body.offsetHeight - 256 &&
      this.state.queryState !== BrowseQueryState.LoadingMore &&
      this.state.resultsLimit <= this.state.entities?.length
    ) {
      this.update({resultsLimit: this.state.resultsLimit + 100});
      await this.fetchEntities(null, true);
    }
  }, 200);

  getTitle(): string {
    if (!this.shouldShowSearchInput()) {
      return this.params.searchText ? `Search results for “${this.params.searchText}”` : null;
    }

    return {
      [BrowseMode.Add]: `Add Existing Report`,
      [BrowseMode.Browse]: `Browse`,
    }[this.params.mode];
  }

  getAvailableReports() {
    let reports = getOrderedSearchContextDisplayOptions();
    if (this.isAddMode()) {
      reports = reports.filter((report) => {
        const reportFromContext = getReportNameFromSearchContext(report);
        return getDashboardAllowedReports().includes(reportFromContext);
      });
    }
    return reports;
  }

  track(event: string, props: Obj = {}) {
    if (CAPTURED_TRACKING.has(event)) {
      mixpanel.track(event, {
        ...this.getCommonTrackingProperties(),
        ...props,
        project_id: this.params.projectId,
      });
    }
  }

  resetFilters() {
    this.updateQueryParams({
      filter: {[FilterType.ShareStatus]: [], [FilterType.Report]: [], [FilterType.CreatorEmail]: []},
    });
  }

  getCommonTrackingProperties() {
    const {entities} = this.state;
    const searchedText = this.params.searchText || ``;

    const searchSource = this.isFullPageMode() ? SearchSource.Discover : SearchSource.Modal;

    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.ContentDiscoveryType]: searchedText
        ? ContentDiscoveryType.SearchQuery
        : ContentDiscoveryType.Browse,
      [SearchTrackedProperty.SortValue]: this.state.queryParams.sort,
      [SearchTrackedProperty.Source]: searchSource,
      [SearchTrackedProperty.ResultAmount]: entities?.length || 0,
      [SearchTrackedProperty.SearchQuery]: searchedText,
      [SearchTrackedProperty.BrowseMode]: this.params.mode,
      [SearchTrackedProperty.NumCreatorFilters]: this.state.queryParams.filter[FilterType.CreatorEmail].length,
      [SearchTrackedProperty.ResultMode]: this.state.resultsMode,
    };
  }

  getQueryFilters(queryParams: BrowseQueryParams): SearchQueryFilters {
    let queryTypes = queryParams.filter[FilterType.Report];
    if (!queryTypes.length) {
      queryTypes = this.getAvailableReports();
    }
    const queryTypeFilters = queryTypes.map((value) => ({value, negated: false}));
    const shareStatusSet = new Set(queryParams.filter[FilterType.ShareStatus]);
    const queryUserFilters = queryParams.filter[FilterType.CreatorEmail].map((value: string) => ({
      value,
      negated: false,
    }));

    if (shareStatusSet.has(ShareStatusFilter.Shared) && !shareStatusSet.has(ShareStatusFilter.Owner)) {
      queryUserFilters.push({
        value: `me`,
        negated: true,
      });
    }

    if (shareStatusSet.has(ShareStatusFilter.Owner) && !shareStatusSet.has(ShareStatusFilter.Shared)) {
      queryUserFilters.push({
        value: `me`,
        negated: false,
      });
    }

    const queryEventFilters =
      FilterType.Event in queryParams.filter
        ? queryParams.filter[FilterType.Event].map((eventName: string) => ({
            value: eventName,
            negated: false,
          }))
        : [];

    return {
      email: queryUserFilters,
      type: queryTypeFilters,
      ...(queryEventFilters.length > 0 ? {event: queryEventFilters} : {}),
    };
  }

  getQuerySortParams(sort: SearchSortOption) {
    switch (sort) {
      case SearchSortOption.Popularity:
        return {
          key: SearchSortKey.Popularity,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.LastModified:
        return {
          key: SearchSortKey.LastModified,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.TitleAscending:
        return {
          key: SearchSortKey.Name,
          order: SearchSortOrder.Ascending,
        };
      case SearchSortOption.TitleDescending:
        return {
          key: SearchSortKey.Name,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.RecentlyViewed:
        return {
          key: SearchSortKey.RecentlyViewed,
          order: SearchSortOrder.Descending,
        };
      default:
        return null;
    }
  }

  updateQueryParams(newQueryParams: Partial<BrowseQueryParams>, options: {customSearchText?: string} = {}) {
    this.update({
      queryParams: {
        ...this.state.queryParams,
        ...newQueryParams,
      },
    });
    this.fetchEntities(options.customSearchText);

    this.updatePersistedState();
  }

  setDefaultState(paramUpdate: BrowseParams) {
    // searchText=`` means "I don't want to set a search text".
    // searchText=null means "I want to set the search text to the default (persisted) value".
    // same for other filters

    const persistedState = this.getPersistedState();

    const persistedQueryParams: Partial<BrowseQueryParams> = persistedState?.queryParams || {filter: {}};
    // only set the params if they are not null or undefined
    const paramsQueryParams = {
      filter: {
        ...(!isNil(paramUpdate.creatorFilter)
          ? {[FilterType.CreatorEmail]: paramUpdate.creatorFilter === `` ? [] : [paramUpdate.creatorFilter]}
          : {}),
        ...(!isNil(paramUpdate.entityTypeFilter)
          ? {[FilterType.Report]: paramUpdate.entityTypeFilter === `` ? [] : [paramUpdate.entityTypeFilter]}
          : {}),
        ...(!isNil(paramUpdate.eventNameFilter)
          ? {[FilterType.Event]: paramUpdate.eventNameFilter === `` ? [] : [paramUpdate.eventNameFilter]}
          : {}),
      },
      ...(paramUpdate.sort ? {sort: paramUpdate.sort} : {}),
      // when the search text is updated, we need to set the sort to relevance
      ...(paramUpdate.searchText ? {sort: SearchSortOption.Relevance} : {}),
      // is searchText was set, but it was cleared, we need to set the sort to popularity
      ...(this.params.searchText && !paramUpdate.searchText ? {sort: SearchSortOption.Popularity} : {}),
    };

    // if paramsQueryParams values are not empty, it means that we received custom params
    const hasParamDefaults = !isEmpty(paramsQueryParams.filter);

    const resultsMode = persistedState && !hasParamDefaults ? persistedState.resultsMode : BrowseResultsMode.Table;

    const queryParams = {
      ...DEFAULT_QUERY_PARAMS,
      ...persistedQueryParams,
      ...paramsQueryParams,
      filter: {
        ...DEFAULT_QUERY_PARAMS.filter,
        ...persistedQueryParams.filter,
        ...paramsQueryParams.filter,
      },
    };

    this.update({
      resultsMode,
    });

    // searchText is still not set as the new params.searchText, so we need to set it manually
    this.updateQueryParams(queryParams, {customSearchText: paramUpdate.searchText});
  }

  async updatePersistedState() {
    if (this.isFullPageMode()) {
      const value: BrowsePersistedState = {
        queryParams: {
          ...this.state.queryParams,
          ...(this.state.queryParams.sort !== SearchSortOption.Relevance ? {sort: this.state.queryParams.sort} : {}),
        },
        resultsMode: this.state.resultsMode,
      };
      await this.persistedStateStorage.setItem({userId: this.params.userId, key: this.params.projectId, value});
    }
  }

  getPersistedState(): BrowsePersistedState | null {
    if (this.isFullPageMode()) {
      return this.persistedStateStorage.getItem({userId: this.params.userId, key: this.params.projectId});
    }
    return null;
  }

  private fetchEntities = debounce(
    async (customSearchText?: string, loadingMore?: boolean) => {
      const currentRequestIndex = this.state.queryRequestIndex + 1;
      this.update({
        queryState: loadingMore ? BrowseQueryState.LoadingMore : BrowseQueryState.Loading,
        queryRequestIndex: currentRequestIndex,
      });

      // clone query params because the reference could be updated while a search request is in flight
      const queryParams = cloneDeep(this.state.queryParams);
      const searchedText = isNil(customSearchText) ? this.params.searchText || `` : customSearchText;

      const trackingProps = {
        [SearchTrackedProperty.OwnerFilters]: queryParams.filter[FilterType.ShareStatus],
        [SearchTrackedProperty.ReportFilters]: queryParams.filter[FilterType.Report],
        [SearchTrackedProperty.EventFilters]: queryParams.filter[FilterType.Event],
        [SearchTrackedProperty.SearchQuery]: searchedText,
        [SearchTrackedProperty.SortValue]: queryParams.sort,
      };

      const queryFilters = this.getQueryFilters(queryParams);

      try {
        const timeBeforeQuery = Date.now();
        // always force empty queries to hit the search endpoint for latest results
        const results = await searchUtils.executeSearch({
          queryFilters,
          searchedText,
          querySort: this.getQuerySortParams(queryParams.sort),
          fromFetchCache: !!searchedText,
          limit: this.state.resultsLimit,
          sharedOnly: true,
        });

        if (currentRequestIndex === this.state.queryRequestIndex && results) {
          this.update({
            queryState: BrowseQueryState.Done,
            lastSearchedText: searchedText,
            entities: results,
          });

          // add searchedText to recent only after the query returned results succesfully
          if (searchedText !== DEFAULT_SEARCH_QUERY_STRING) {
            searchUtils.addRecentItem({
              icon: `search`,
              key: `${RecentSearchItemType.SearchTerm}-${searchedText}`,
              label: searchedText,
              type: RecentSearchItemType.SearchTerm,
            } as RecentSearchItem);
          }
        }

        this.track(BrowseTrackedEvent.SearchResultLoaded, {
          ...trackingProps,
          ...getTrackingPropertiesForResultsLoad(results),
          [SearchTrackedProperty.QueryTime]: Date.now() - timeBeforeQuery,
        });
      } catch (err) {
        reporter.error(err);
        this.update({
          queryState: BrowseQueryState.Failed,
          entities: null,
        });
      }
    },
    200,
    {leading: true},
  );

  handleEntityClick(entity: SearchResult) {
    this.track(BrowseTrackedEvent.ResultSelected, {
      ...getTrackingPropertiesForResult(entity),
      [SearchTrackedProperty.ResultPosition]: this.state.entities.findIndex((result) => entity.id === result.id),
      [SearchTrackedProperty.ResultAmount]: this.state.entities.length,
    });
  }

  handleEntityContextMenu(entity: SearchResult) {
    this.track(BrowseTrackedEvent.ResultRightClicked, {
      ...getTrackingPropertiesForResult(entity),
      [SearchTrackedProperty.ResultPosition]: this.state.entities.findIndex((result) => entity.id === result.id),
      [SearchTrackedProperty.ResultAmount]: this.state.entities.length,
    });
  }

  hasStarterBoard() {
    return Boolean(this.state.entities?.find((entity) => entity.template_type === TemplateType.STARTER_BOARD));
  }

  async showStarterBoardPrompt() {
    const viewedStarterBoardWithData = (await getUserFlags()).VIEWED_STARTER_BOARD_WITH_DATA_COUNT > 0;
    this.update({showStarterBoardPrompt: !viewedStarterBoardWithData});
  }

  redirectToStarterBoard(ev: MouseEvent) {
    ev.stopPropagation();

    const starterBoardEntity = this.state.entities.find(
      (entity) => entity.template_type === TemplateType.STARTER_BOARD,
    );

    this.track(BrowseTrackedEvent.StarterBoardPromptButtonClicked);
    if (shouldOpenInNewTab(ev)) {
      window.open(starterBoardEntity.link, `_blank`);
    } else {
      this.getContext(`urlManager`).setLocation(starterBoardEntity.link);
      this.params.onRedirectToEntity?.();
    }
  }
}

export const BrowseEl = defineAndMakeJsxFactory<JsxProps>(`mp-browse`, Browse);
export const BrowseReact = panel2react<JsxProps>(`mp-browse`, Browse);
